
import React, { useMemo, useState, useCallback } from 'react';
import type { ActiveCartItem, Language, ShoppingCart } from '../types';
import BeefIcon from './icons/BeefIcon';
import UtensilsIcon from './icons/UtensilsIcon';
import LeafIcon from './icons/LeafIcon';
import CartIcon from './icons/CartIcon';
import ShoppingBagIcon from './icons/ShoppingBagIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import ChevronUpIcon from './icons/ChevronUpIcon';
import PencilIcon from './icons/PencilIcon';
import BeakerIcon from './icons/BeakerIcon';
import PlusIcon from './icons/PlusIcon';
import CheckCircle2 from './icons/CheckCircle2';
import SmartUnitCapsule from './SmartUnitCapsule';
import { getTranslation, isRTL } from '../utils/translations';
import { unifyUnit, formatIngredientQuantity } from '../utils/unitConversion';
import {
    ALL_PILLARS,
    buildListPillars,
    buildActiveCartPillarIds,
    getPillarDisplayName,
    resolveItemPillar,
    type PillarId,
} from '../utils/shoppingCategories';
import { swapVisiblePillarsInOrder } from '../utils/activeCartSnapshot';

export type { PillarId } from '../utils/shoppingCategories';
export { ALL_PILLARS } from '../utils/shoppingCategories';

interface SmartShoppingListProps {
    items: ActiveCartItem[];
    onToggleCheck: (id: string, checked: boolean) => void;
    onUpdateTotalQty: (itemId: string, newTotal: number) => void;
    onUpdateItem?: (itemId: string, updates: Partial<ActiveCartItem>) => void;
    selectionMode: boolean;
    selectedIds: Set<string>;
    onToggleSelect: (id: string) => void;
    language?: Language;
    baseFontSize?: number;
    onEditItem?: (item: ActiveCartItem) => void;
    onDeleteItem?: (itemId: string) => void;
    collapsedPillars: Set<string>;
    onTogglePillar: (id: string) => void;
    expandedItemIds: Set<string>;
    onToggleItemExpansion: (id: string) => void;
    categoryOverrides?: Record<string, string>;
    undoItem?: { id: string; name: string; wasDone: boolean } | null;
    onUndo?: () => void;
    pillarOrder?: string[];
    onReorderPillars?: (newOrder: string[]) => void;
    shoppingCarts?: ShoppingCart[];
    onAddNewItemWithCategory?: (category: string) => void;
    hideDone?: boolean;
}

const SmartShoppingList: React.FC<SmartShoppingListProps> = ({
    items,
    onToggleCheck,
    onUpdateTotalQty,
    onUpdateItem,
    selectionMode,
    selectedIds,
    onToggleSelect,
    language,
    baseFontSize = 16,
    collapsedPillars,
    onTogglePillar,
    expandedItemIds,
    onToggleItemExpansion,
    categoryOverrides,
    undoItem,
    onUndo,
    pillarOrder,
    onReorderPillars,
    shoppingCarts = [],
    onAddNewItemWithCategory,
    hideDone = false,
}) => {
    const t = (key: any) => getTranslation(language, key);
    const rtl = isRTL(language);
    const listPillars = useMemo(() => buildListPillars(shoppingCarts), [shoppingCarts]);
    const allPillarIds = useMemo(() => buildActiveCartPillarIds(shoppingCarts), [shoppingCarts]);

    const pillars = useMemo(() => {
        const data: Record<string, { active: ActiveCartItem[]; done: ActiveCartItem[] }> = {};
        allPillarIds.forEach((pillarId) => {
            data[pillarId] = { active: [], done: [] };
        });

        items.forEach(item => {
            const pillarId = resolveItemPillar(item, categoryOverrides, listPillars);
            const targetPillar = data[pillarId] ? pillarId : 'others';
            
            // Bidirectional Delay Logic:
            // 1. Just Checked (Active -> Done): item.done is true, wasDone was false. 
            //    STAY in 'active' until undo vanishes.
            const isJustChecked = undoItem?.id === item.id && item.done && !undoItem.wasDone;
            
            // 2. Just Unchecked (Done -> Active): item.done is false, wasDone was true.
            //    STAY in 'done' until undo vanishes.
            const isJustUnchecked = undoItem?.id === item.id && !item.done && undoItem.wasDone;
            
            if ((item.done || isJustUnchecked) && !isJustChecked) {
                data[targetPillar].done.push(item);
            } else {
                data[targetPillar].active.push(item);
            }
        });
        return data;
    }, [items, undoItem, categoryOverrides, listPillars, allPillarIds]);

    const toggleItemExpansionInternal = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        onToggleItemExpansion(id);
    };

    const pillarConfigs = useMemo((): { id: string; icon: React.ReactNode; colorClass: string; bgColor: string; label?: string }[] => [
        { id: 'manual', icon: <PencilIcon className="w-5 h-5" />, colorClass: "text-amber-600 dark:text-amber-400", bgColor: "bg-amber-50/50 dark:bg-amber-900/20" },
        { id: 'meat', icon: <BeefIcon className="w-5 h-5" />, colorClass: "text-rose-600 dark:text-rose-400", bgColor: "bg-rose-50/50 dark:bg-rose-900/20" },
        { id: 'fruits', icon: <div className="text-xl">🍎</div>, colorClass: "text-red-500 dark:text-red-400", bgColor: "bg-red-50/30 dark:bg-red-900/20" },
        { id: 'vegetables', icon: <LeafIcon className="w-5 h-5" />, colorClass: "text-emerald-600 dark:text-emerald-400", bgColor: "bg-emerald-50/50 dark:bg-emerald-900/20" },
        { id: 'dairy', icon: <div className="text-xl">🥛</div>, colorClass: "text-blue-600 dark:text-blue-400", bgColor: "bg-blue-50/50 dark:bg-blue-900/20" },
        { id: 'spices', icon: <div className="text-xl">🌶️</div>, colorClass: "text-orange-700 dark:text-orange-400", bgColor: "bg-orange-50/40 dark:bg-orange-900/20" },
        { id: 'snacks', icon: <div className="text-xl">🍿</div>, colorClass: "text-yellow-700 dark:text-yellow-400", bgColor: "bg-yellow-50/50 dark:bg-yellow-900/20" },
        { id: 'babyProducts', icon: <div className="text-xl">🍼</div>, colorClass: "text-pink-600 dark:text-pink-400", bgColor: "bg-pink-50/50 dark:bg-pink-900/20" },
        { id: 'cleaning', icon: <BeakerIcon className="w-5 h-5" />, colorClass: "text-purple-600 dark:text-purple-400", bgColor: "bg-purple-50/50 dark:bg-purple-900/20" },
        { id: 'toiletries', icon: <div className="text-xl">🪥</div>, colorClass: "text-pink-500 dark:text-pink-400", bgColor: "bg-pink-50/50 dark:bg-pink-900/20" },
        { id: 'canned', icon: <div className="text-xl">🥫</div>, colorClass: "text-orange-600 dark:text-orange-400", bgColor: "bg-orange-50/50 dark:bg-orange-900/20" },
        { id: 'pantry', icon: <div className="text-xl">🥖</div>, colorClass: "text-amber-800 dark:text-amber-300", bgColor: "bg-orange-100/30 dark:bg-amber-900/20" },
        { id: 'frozen', icon: <div className="text-xl">❄️</div>, colorClass: "text-sky-600 dark:text-sky-400", bgColor: "bg-sky-50/50 dark:bg-sky-900/20" },
        { id: 'bakery', icon: <div className="text-xl">🥐</div>, colorClass: "text-amber-600 dark:text-amber-400", bgColor: "bg-amber-50/50 dark:bg-amber-900/20" },
        { id: 'beverages', icon: <div className="text-xl">🥤</div>, colorClass: "text-teal-600 dark:text-teal-400", bgColor: "bg-teal-50/50 dark:bg-teal-900/20" },
        { id: 'generalFood', icon: <UtensilsIcon className="w-5 h-5" />, colorClass: "text-indigo-600 dark:text-indigo-400", bgColor: "bg-indigo-50/50 dark:bg-indigo-900/20" },
        { id: 'general', icon: <CartIcon className="w-5 h-5" />, colorClass: "text-slate-600 dark:text-slate-400", bgColor: "bg-slate-50 dark:bg-slate-800" },
        { id: 'other', icon: <ShoppingBagIcon className="w-5 h-5" />, colorClass: "text-slate-500 dark:text-slate-400", bgColor: "bg-slate-50/80 dark:bg-slate-800/80" },
        { id: 'others', icon: <ShoppingBagIcon className="w-5 h-5" />, colorClass: "text-sky-600 dark:text-sky-400", bgColor: "bg-sky-50/30 dark:bg-sky-900/20" },
        ...listPillars.map((pillar) => ({
            id: pillar.id,
            label: pillar.name,
            icon: <CartIcon className="w-5 h-5" />,
            colorClass: "text-sky-700 dark:text-sky-300",
            bgColor: "bg-sky-50/50 dark:bg-sky-900/20",
        })),
    ], [listPillars]);

    const effectivePillarOrder = useMemo((): string[] => {
        if (pillarOrder && pillarOrder.length > 0) {
            const known = pillarOrder.filter((id) => allPillarIds.includes(id));
            const missing = allPillarIds.filter((id) => !known.includes(id));
            return [...known, ...missing];
        }
        return allPillarIds;
    }, [pillarOrder, allPillarIds]);

    const orderedPillarConfigs = useMemo(() => {
        const orderIndex = new Map(effectivePillarOrder.map((id, idx) => [id, idx]));
        return [...pillarConfigs].sort((a, b) => {
            const ai = orderIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER;
            const bi = orderIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER;
            return ai - bi;
        });
    }, [effectivePillarOrder, pillarConfigs]);

    const visiblePillarIds = useMemo(() => {
        const withItems = orderedPillarConfigs.filter((config) => {
            const group = pillars[config.id];
            if (!group) return false;
            if (hideDone) {
                return group.active.length > 0;
            }
            return group.active.length > 0 || group.done.length > 0;
        });

        // Sort: if a category is all done, move to the bottom.
        const sorted = [...withItems].sort((a, b) => {
            const groupA = pillars[a.id];
            const groupB = pillars[b.id];
            const aAllDone = groupA && groupA.active.length === 0 && groupA.done.length > 0;
            const bAllDone = groupB && groupB.active.length === 0 && groupB.done.length > 0;

            if (aAllDone && !bAllDone) return 1;
            if (!aAllDone && bAllDone) return -1;
            return 0;
        });

        return sorted.map((config) => config.id);
    }, [orderedPillarConfigs, pillars, hideDone]);

    const handleMovePillar = useCallback((pillarId: string, direction: 'up' | 'down') => {
        if (!onReorderPillars) return;
        const nextOrder = swapVisiblePillarsInOrder(
            effectivePillarOrder,
            visiblePillarIds,
            pillarId,
            direction,
        );
        if (nextOrder.join('|') !== effectivePillarOrder.join('|')) {
            onReorderPillars(nextOrder);
        }
    }, [effectivePillarOrder, onReorderPillars, visiblePillarIds]);

    const renderItemRow = (item: ActiveCartItem) => {
        const isChecked = item.done;
        const isSelected = selectedIds.has(item.id);
        const isExpanded = expandedItemIds.has(item.id);
        const totalQuantity = item.sources.reduce((acc, s) => acc + s.quantity, 0);
        
        // Auto-convert g to kg if >= 1000 for display
        let displayQty = totalQuantity;
        let displayUnit = item.unit;
        
        if (unifyUnit(item.unit) === 'g' && totalQuantity >= 1000) {
            displayQty = totalQuantity / 1000;
            displayUnit = 'kg';
        }

        const transKey = 'ingredient_' + item.name.toLowerCase();
        const translated = t(transKey);
        const displayName = translated !== transKey ? translated : item.name;

        if (item.id === undoItem?.id) {
            return (
                <div key={item.id} className="relative overflow-hidden bg-slate-950 dark:bg-slate-100 py-3 px-6 group min-h-[64px] flex flex-col justify-center">
                     <div className="flex items-center justify-between">
                        <div className="flex flex-col min-w-0">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-0.5 leading-none transition-colors">{t('itemMoved')}</span>
                            <span className="text-white dark:text-slate-900 font-bold truncate leading-tight" style={{ fontSize: baseFontSize + 'px' }}>{displayName}</span>
                        </div>
                        <button 
                            onClick={(e) => { e.stopPropagation(); onUndo?.(); }}
                            className="bg-brand-orange text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-all active:scale-95 shadow-lg shadow-brand-orange/20 mr-1 rtl:mr-0 rtl:ml-1"
                        >
                            {t('undo')}
                        </button>
                     </div>
                     {/* Strip Loader */}
                     <div className="absolute bottom-0 left-0 h-1 bg-brand-orange animate-strip-loader" />
                </div>
            );
        }

        return (
            <div key={item.id} className="flex flex-col border-b border-gray-100 dark:border-slate-800 last:border-0">
                <div 
                    onClick={() => selectionMode ? onToggleSelect(item.id) : onToggleCheck(item.id, !isChecked)}
                    className={`flex items-center justify-between py-3 px-6 transition-all cursor-pointer ${isChecked ? 'bg-slate-50/50 dark:bg-slate-800/30 opacity-60' : 'hover:bg-slate-50/80 dark:hover:bg-slate-800/50'} ${isSelected ? 'bg-sky-50 dark:bg-sky-900/20' : ''}`}
                >
                    {/* Name Group */}
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                        {!selectionMode && (
                            <button 
                                onClick={(e) => toggleItemExpansionInternal(e, item.id)}
                                className={`text-slate-300 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-400 transition-all p-1 -ml-1 ${isExpanded ? 'rotate-180' : ''}`}
                            >
                                <ChevronDownIcon className="w-5 h-5" />
                            </button>
                        )}

                        {selectionMode && (
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${isSelected ? 'border-sky-500 bg-sky-500 text-white shadow-lg' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm'}`}>
                                {isSelected && <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={4} stroke="currentColor"><path d="M4.5 12.75l6 6 9-13.5" /></svg>}
                            </div>
                        )}
                        
                        <div className="flex items-center gap-3 min-w-0 flex-grow">
                            <span 
                                className={`font-bold tracking-tight transition-all ${isChecked ? 'text-slate-300 dark:text-slate-600 line-through' : 'text-slate-800 dark:text-slate-200'} ${isExpanded ? 'whitespace-normal break-words overflow-visible' : 'truncate max-[350px]:whitespace-normal max-[350px]:break-words max-[350px]:overflow-visible'}`}
                                style={{ fontSize: baseFontSize + 'px' }}
                            >
                                {displayName}
                            </span>
                        </div>
                    </div>

                    {/* Quantity Group */}
                    <div className="flex items-center gap-2 flex-shrink-0 pl-2 rtl:pl-0 rtl:pr-2" onClick={e => e.stopPropagation()}>
                        {!isChecked && !selectionMode ? (
                            <SmartUnitCapsule 
                                value={displayQty}
                                unit={displayUnit}
                                onChange={(val, unit) => {
                                    // If we are in the auto-converted state (stored as g, displayed as kg)
                                    // and the user edits the value keeping it as kg, convert back to g for storage.
                                    if (unifyUnit(item.unit) === 'g' && unit === 'kg' && displayUnit === 'kg') {
                                        onUpdateTotalQty(item.id, val * 1000);
                                    } else {
                                        // Standard behavior
                                        onUpdateTotalQty(item.id, val);
                                        if (unit !== item.unit && onUpdateItem) {
                                            onUpdateItem(item.id, { unit });
                                        }
                                    }
                                }}
                                language={language}
                                isMeat={item.isMeat}
                            />
                        ) : (
                            <div className={`
                                flex items-center justify-center gap-1.5 px-3 h-8 rounded-lg text-sm font-bold border opacity-80
                                ${item.isMeat 
                                    ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 border-rose-100 dark:border-rose-900' 
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700'}
                            `}>
                                <span className="font-mono">{parseFloat(displayQty.toFixed(2))}</span>
                                <span className="text-xs uppercase tracking-tight">{t('unit_' + displayUnit)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {isExpanded && !selectionMode && (
                    <div className="mx-6 mb-4 bg-slate-50/50 dark:bg-slate-800/30 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 text-[11px] animate-fade-in shadow-inner">
                        <ul className="space-y-1.5">
                            {item.sources.map((s) => {
                                let originDisplay = s.originName;
                                if (s.originName === 'Manual Adjustment') originDisplay = t('manualAdjustment');
                                if (s.originName === 'Manual') originDisplay = t('manual');
                                
                                // Try to display date if originId looks like a date (YYYY-MM-DD)
                                let dateDisplay = '';
                                if (s.originType === 'day' && s.originId && /^\d{4}-\d{2}-\d{2}$/.test(s.originId)) {
                                    // simple parse to show day/month
                                    const parts = s.originId.split('-');
                                    if (parts.length === 3) dateDisplay = `${parts[2]}/${parts[1]}`;
                                }

                                return (
                                    <li key={s.id} className="flex justify-between items-center text-slate-500 dark:text-slate-400">
                                        <div className="flex items-center gap-1.5 overflow-hidden">
                                            <span className="font-bold truncate">{originDisplay}</span>
                                            {dateDisplay && <span className="text-slate-400 dark:text-slate-600 font-medium">({dateDisplay})</span>}
                                        </div>
                                        <span className="font-mono font-black ltr:ml-4 rtl:mr-4 flex-shrink-0">{formatIngredientQuantity(s.quantity, item.unit, t)}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    if (items.length === 0) {
        return (
            <div className="py-24 text-center flex flex-col items-center justify-center gap-4 animate-fade-in">
                <div className="p-6 bg-brand-sage/10 dark:bg-brand-sage/20 rounded-[2rem] text-brand-sage/60 dark:text-brand-sage/50">
                    <CartIcon className="w-16 h-16" strokeWidth={1.25} />
                </div>
                <p className="text-slate-400 dark:text-slate-500 font-black uppercase tracking-[0.2em] text-xs">{t('cartIsEmpty')}</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col pb-6 space-y-6">
            {visiblePillarIds.map(pillarId => {
                const config = orderedPillarConfigs.find(c => c.id === pillarId);
                if (!config) return null;
                const group = pillars[config.id];
                const isCollapsed = collapsedPillars.has(config.id);
                if (group.active.length === 0 && group.done.length === 0) return null;

                const isCategoryDone = group.active.length === 0 && group.done.length > 0;

                const headerBgClass = isCategoryDone
                    ? 'bg-emerald-50 border-b border-emerald-100 dark:bg-emerald-950/20 dark:border-emerald-900/30'
                    : 'bg-gray-50 border-b border-gray-100 dark:bg-slate-900/50 dark:border-slate-800';

                const headerTextClass = isCategoryDone
                    ? 'text-emerald-800 dark:text-emerald-300'
                    : 'text-slate-800 dark:text-slate-100';

                const titleColorClass = isCategoryDone
                    ? 'text-emerald-800 dark:text-emerald-300'
                    : config.colorClass;

                const visibleIndex = visiblePillarIds.indexOf(config.id);
                const canMoveUp = selectionMode && visibleIndex > 0;
                const canMoveDown = selectionMode && visibleIndex >= 0 && visibleIndex < visiblePillarIds.length - 1;

                return (
                    <div
                        key={config.id}
                        className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden"
                    >
                        <div 
                            onClick={() => onTogglePillar(config.id)}
                            className={`flex items-center justify-between px-6 py-4 hover:opacity-90 cursor-pointer ${headerBgClass}`}
                        >
                            {/* Start Group: Title & Icon */}
                            <div className="flex items-center gap-3 min-w-0">
                                {selectionMode && onReorderPillars && (
                                    <div className="flex flex-col shrink-0" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            type="button"
                                            disabled={!canMoveUp}
                                            onClick={() => handleMovePillar(config.id, 'up')}
                                            className="p-0.5 text-slate-300 dark:text-slate-600 hover:text-sky-600 dark:hover:text-sky-300 disabled:opacity-20 disabled:pointer-events-none"
                                            aria-label="Move category up"
                                        >
                                            <ChevronUpIcon className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            disabled={!canMoveDown}
                                            onClick={() => handleMovePillar(config.id, 'down')}
                                            className="p-0.5 text-slate-300 dark:text-slate-600 hover:text-sky-600 dark:hover:text-sky-300 disabled:opacity-20 disabled:pointer-events-none"
                                            aria-label="Move category down"
                                        >
                                            <ChevronDownIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                                <div className={`${titleColorClass} opacity-80 shrink-0`}>{config.icon}</div>
                                <h4 className={`text-base font-black uppercase tracking-widest truncate ${titleColorClass}`}>
                                    {config.label ?? getPillarDisplayName(config.id, listPillars, t)}
                                </h4>
                            </div>

                            {/* End Group: Count & Chevron */}
                            <div className="flex items-center gap-3">
                                {isCategoryDone && (
                                    <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
                                )}
                                {onAddNewItemWithCategory && !isCollapsed && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAddNewItemWithCategory(config.id);
                                        }}
                                        className="w-8 h-8 rounded-full bg-brand-orange hover:bg-orange-600 active:scale-95 text-white flex items-center justify-center shadow-sm shadow-brand-orange/10 border border-white/10 shrink-0"
                                        title={t('add') || 'Add'}
                                    >
                                        <PlusIcon className="w-4 h-4" />
                                    </button>
                                )}
                                <div className={`h-8 px-2 rounded-full flex items-center justify-center min-w-[2rem] w-auto ${isCategoryDone ? 'bg-emerald-100/50 dark:bg-emerald-900/30' : 'bg-slate-200/50 dark:bg-slate-700/50'}`}>
                                    <span className={`text-[11px] font-black ${isCategoryDone ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'}`}>
                                        {group.done.length}/{group.active.length + group.done.length}
                                    </span>
                                </div>
                                <div className={`${isCategoryDone ? 'text-emerald-400 dark:text-emerald-600' : 'text-slate-300 dark:text-slate-600'} transition-transform duration-300`} style={{ transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}>
                                    <ChevronDownIcon className="w-5 h-5" />
                                </div>
                            </div>
                        </div>
                        {!isCollapsed && (
                            <div className="flex flex-col bg-white dark:bg-slate-900">
                                {isCategoryDone && (
                                    <div className="py-4 flex items-center justify-center border-b border-slate-50 dark:border-slate-800/50 animate-fade-in">
                                        <span className="text-sm font-medium text-emerald-600/70 dark:text-emerald-500/50">{t('allCollected')}</span>
                                    </div>
                                )}
                                {group.active.map(item => renderItemRow(item))}
                                {group.active.length > 0 && group.done.length > 0 && !hideDone && <div className="h-px bg-slate-50 dark:bg-slate-800 mx-6 my-1" />}
                                {!hideDone && group.done.map(item => renderItemRow(item))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default SmartShoppingList;
